

import { Box, Chip, Divider, FormControl, Grid2, InputLabel, MenuItem, Paper, Select, Skeleton, Tab, Tabs, Typography, useTheme } from "@mui/material";
import React, { FC, useEffect, useMemo } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { ButtonStepMenu } from "../../../Components/Form/ButtonStepMenu/button-step-menu.component";
import { KanbanBoardFinancial } from "../../../Components/KanbanBoardFinancial/kanban-board.component";
import { Placeholder } from "../../../Components/Placeholder/placeholder.component";
import { SaleProductTable } from "../../../Components/Table/variants/sale-product-table/sale-product-table.component";
import { BaseContentPage } from "../../../Components/Templates/BaseContentPage/base-content-page.componen";
import { BaseScreen } from "../../../Components/Templates/BaseScreen/basescreen.component";
import { ServiceOrderStageEnum } from "../../../Services/api/service-orders/service-order.interface";
import { BillProductOrderOrdersUseCase } from "../../../Usecase/Orders/bill-products-order-usecase";
import { ListOrdersUseCase } from "../../../Usecase/Orders/list-orders-usecase";
import { ShowOmieOrderDetailsUseCase } from "../../../Usecase/Orders/show-omie-order-details-usecase";
import { ShowOrdersUseCase } from "../../../Usecase/Orders/show-order-usecase";
import { labelsOrderProductionStage } from "../../../utils/normalizeProductionStages";
import { ISaleEntity, useDetailsFinancial } from "./financial-details.hook";

import { FaUser, FaUserAltSlash } from "react-icons/fa";
import { Button } from "../../../Components/Form/Button/button.scomponent";
import { ButtonActionMenu } from "../../../Components/Form/ButtonActionsMenu/button-action-menu.component";
import { Table } from "../../../Components/Table/table.component";
import { Tag } from "../../../Components/Tag/tag.component";
import { normalizeTimeStatusColors } from "../../../utils/normalizeTimeStatus";
import { ConfirmDialog } from '../../../Components/Dialog/variants/dialog-confirmation/dialog-confirmation.component';
import { UpdateOrdersUseCase } from '../../../Usecase/Orders/update-order-usecase';
import SelectSellerVariant from '../../../Components/Modals/variants/select-seller/select-seller-variant.component';
import { ListSellersUseCase } from '../../../Usecase/Sellers/list-sellers-usecase';
import { Can } from '../../../Components/Can';
import { Input } from '../../../Components/Form/Input/input.component';
import Notice from '../../../Components/Notice/notice.component';
import { BillingFlowEnum, OrderStatusEnum, TBillingFlow } from '../../../Services/api/orders/orders.interface';
import { billingFlowLabels, billingFlowOptions, billingFlowTagColors, getBillingFlowDivergenceMessage, getBillingFlowSuggestion, getOrderBillingFlow, getOrderCouponCode, getOrderNsNumber } from '../../../utils/billingFlow';
import { FormatMoney } from '../../../utils/formatMoney';
import { formatSaleAddress, resolvePaymentDetails } from '../../../utils/build-sale-from-order';
import { OrderHistoryTimeline } from '../../../Components/OrderHistoryTimeline/order-history-timeline.component';
import { GetOrderAuditTimelineUseCase } from '../../../Usecase/Audit/get-order-audit-timeline-usecase';
import { normalizeOrderStatusColors, normalizeOrderStatusTitle } from '../../../utils/normalizeOrderStatus';
import { CustomTabPanel } from '../../../Components/Tabs/CustomTabPanel/custom-tab-panel.component';
import { TabScrollArea } from '../../../Components/Tabs/TabScrollArea/tab-scroll-area.component';
import { OrderProductionBatches, OrderProductionSummary } from '../../../Components/OrderProductionBatches/order-production-batches.component';

interface IFinancialDetailsProps {
    billProductOrderOrdersUseCase: BillProductOrderOrdersUseCase;
    listOrdersUseCase: ListOrdersUseCase
    showOrdersUseCase: ShowOrdersUseCase
    showOmieOrderDetailsUseCase: ShowOmieOrderDetailsUseCase
    updateOrdersUseCase: UpdateOrdersUseCase
    listSellersUseCase: ListSellersUseCase
    getOrderAuditTimelineUseCase: GetOrderAuditTimelineUseCase
}

const HCP_FLOW_LABELS: Record<string, string> = {
  AWAITING_ART: 'Arte',
  SHEET: 'Cartela',
  PRODUCTION: 'Produção',
  MACHINE_PRODUCTION: 'Produção de Máquinas',
  LOGISTICS: 'Logística',
}

function getHCPFlowLabel(flow?: string | null): string {
  if (!flow) return 'Não iniciado'
  return HCP_FLOW_LABELS[flow] ?? flow
}

function formatHCPDate(value?: string | null): string {
  if (!value) return '—'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return date.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function getHCPFinishedLabel(value?: boolean | null): string {
  if (value === true) return 'Finalizado'
  if (value === false) return 'Em aberto'
  return 'Não informado'
}

const FinancialDetails : FC<IFinancialDetailsProps> = ({ billProductOrderOrdersUseCase, listOrdersUseCase, showOrdersUseCase, showOmieOrderDetailsUseCase, updateOrdersUseCase, listSellersUseCase, getOrderAuditTimelineUseCase }) => {
    const [value, setValue] = React.useState(0);
    const params = useParams()
    const [searchParams] = useSearchParams()

    const fromOrders = searchParams.get('from') === 'orders-list'
    const backUrl = fromOrders ? '/orders-list' : '/financial-list'
    const detailsBreadcrumb = fromOrders
      ? [{ title: 'Pedidos', link: '/orders-list' }, { title: 'Detalhes' }]
      : [{ title: 'Financeiro', link: '/financial-list' }, { title: 'Detalhes' }]

    const theme = useTheme()

  const { billProductsOrder, handleShowOrder, orderData, kanbanFinancialSchema, kanbanProductionSchema, sale, handleSelectProductToBill, productsToBill, loading, handleDetailsOmieOrder, omieOrderData, setOmieOrderData, handleSelectAllProductsToBill, showModalOrderWithoutSeller, setShowModalOrderWithoutSeller, handleUpdateOrderWithoutSeller, showCustomersModal, handleSelectSeller, setShowCustomersModal, navigate, handleUpdateObservations, handleSubmit, register, handleUpdateBillingFlow, billingCompanyOptions, receivingCompanyOptions, selectedBillingCompanyId, setSelectedBillingCompanyId, selectedReceivingCompanyId, setSelectedReceivingCompanyId, handleUpdateOrderOmieCompanies } = useDetailsFinancial({
    billProductOrderOrdersUseCase,
    listOrdersUseCase,
    showOrdersUseCase,
    orderId: String(params.orderId),
    showOmieOrderDetailsUseCase,
    updateOrdersUseCase
  })

  const LOCKED_STATUSES = [
    OrderStatusEnum.INVOICED,
    OrderStatusEnum.INVOICING,
    OrderStatusEnum.PARTIALLY_INVOICED,
    OrderStatusEnum.PROCESSING_INVOICE,
  ];
  const couponCode = getOrderCouponCode(orderData);
  const nsOrderNumber = getOrderNsNumber(orderData);
  const orderDetailsTitle = `Detalhes - Pedido ${orderData?.nsOrderId || ''}`;
  const flowSuggestion = getBillingFlowSuggestion(couponCode);
  const currentBillingFlow = getOrderBillingFlow(orderData);
  const showBillingFlowSuggestion = !!flowSuggestion && currentBillingFlow === BillingFlowEnum.SALE;
  const isBillingFlowLocked = !!orderData?.status && LOCKED_STATUSES.includes(orderData.status);
  const isOmieCompaniesLocked = !!orderData?.status && [
    OrderStatusEnum.PROCESSING_INVOICE,
    OrderStatusEnum.INVOICING,
    OrderStatusEnum.INVOICED,
  ].includes(orderData.status);
  const billingWarningDescription = getBillingFlowDivergenceMessage(orderData);
  const billingFlowColor = billingFlowTagColors[currentBillingFlow];
  const orderStatusColors = orderData?.status ? normalizeOrderStatusColors(orderData.status) : undefined;
  const orderPayment = (orderData as any)?.orderPayment;
  const paymentDetails = resolvePaymentDetails(orderData);

  const formatOrderMoney = (value?: string | number | null) => FormatMoney(Number(value || 0));
  const formatOrderDate = (value?: string | null) => value ? new Date(value).toLocaleDateString('pt-BR') : 'Não informado';
  const formatAddress = (address?: any) => address?.id
    ? `${address.name || ''} ${address.number || ''}, ${address.locality || ''} - ${address.city || ''} - ${address.province || ''} | ${address.zipcode || ''}`
    : 'Não informado';

  const InfoLine = ({ label, value }: { label: string; value?: React.ReactNode }) => (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: '16px', alignItems: 'flex-start', marginBottom: '8px' }}>
      <Typography sx={{ fontSize: '0.9rem', color: '#667085', fontWeight: 600 }}>{label}</Typography>
      <Typography sx={{ fontSize: '0.95rem', color: '#1f2937', fontWeight: 600, textAlign: 'right', wordBreak: 'break-word' }}>{value ?? 'Não informado'}</Typography>
    </Box>
  );

  const SectionTitle = ({ children }: { children: React.ReactNode }) => (
    <Typography sx={{ fontSize: '1.15rem', fontWeight: 700, marginBottom: '14px' }}>{children}</Typography>
  );

  const columnsProduction = useMemo(() => {
    const createColumns: any[] = [    
      {
        field: 'code',
        headerName: 'Ordem de Produção',
        width: 170,
        renderCell: (params: any) => <Box sx={{ height: '100%', display: 'flex', alignItems: 'center'}}><Typography>{`#${params?.row?.serviceOrder?.externalProductionId || 'Produto sem OP'}`}</Typography></Box>
      },
      
      {
        field: 'description',
        headerName: 'Produto',
        flex:1,
        renderCell: (params: any) => <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', whiteSpace: "normal",  
          wordBreak: "break-word", 
          overflowWrap: "break-word", }}><Typography color='#000' fontSize={'14px'}>{String(params?.row?.product?.name).toUpperCase()}</Typography></Box>
      },
      {
        field: 'quantity',
        headerName: 'Tiragem',
        width: 200,
        renderCell: (params: any) => <Typography>{params.row.totalQuantity ?? params.row.quantity ?? '—'}</Typography>,
      },
      {
        field: 'card',
        headerName: 'Aprovação da Arte',
        flex:1,
        align: 'center',
        renderCell: (params: any) => (
          <Box sx={{ height: '100%', display: 'flex', alignItems: 'center' }}>
            <Typography>{params?.row?.artApprovedDateLabel || 'Não informado'}</Typography>
          </Box>
        ),
      },
      {
        field: 'hcpStatus',
        headerName: 'HCP',
        minWidth: 220,
        flex: 1.25,
        renderCell: (params: any) => {
          if (params.row.productionBatches?.length || params.row.productionSummary) {
            return <Box sx={{ py: 1, width: '100%' }}><OrderProductionSummary
              batches={params.row.productionBatches} summary={params.row.productionSummary}
              commercialQuantity={params.row.totalQuantity ?? params.row.quantity} compact />
            </Box>
          }
          const hasHCP = Boolean(
            params.row.hcpCurrentFlow
            || params.row.hcpCurrentFlowStatusName
            || params.row.hcpCurrentFlowDisplayCode
          )

          return (
            <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 0.5, width: '100%' }}>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                <Chip
                  label={getHCPFlowLabel(params.row.hcpCurrentFlow)}
                  size="small"
                  sx={{
                    height: 22,
                    fontSize: '0.72rem',
                    fontWeight: 700,
                    bgcolor: hasHCP ? 'rgba(21,101,192,0.10)' : 'rgba(0,0,0,0.06)',
                    color: hasHCP ? '#1565c0' : 'text.secondary',
                  }}
                />
                {params.row.hcpCurrentFlowDisplayCode && (
                  <Chip
                    label={params.row.hcpCurrentFlowDisplayCode}
                    size="small"
                    sx={{ height: 22, fontSize: '0.72rem', bgcolor: '#eceff3', color: 'text.secondary' }}
                  />
                )}
              </Box>
              <Typography variant="body2" fontWeight={600} title={params.row.hcpCurrentFlowStatusName ?? undefined}>
                {params.row.hcpCurrentFlowStatusName ?? 'Sem status HCP'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {getHCPFinishedLabel(params.row.hcpFinished)}
              </Typography>
            </Box>
          )
        },
      },
      {
        field: 'hcpDeadline',
        headerName: 'Prazo HCP',
        minWidth: 180,
        flex: 1,
        renderCell: (params: any) => params.row.productionBatches?.length > 1 || params.row.productionSummary?.hasMultiplePositions ? (
          <Typography variant="body2" color="text.secondary">Consulte os prazos de cada lote abaixo.</Typography>
        ) : (
          <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 0.25 }}>
            <Typography variant="caption" color="text.secondary">
              Início
            </Typography>
            <Typography variant="body2" fontWeight={600}>
              {formatHCPDate(params.row.productionBatches?.[0] ? params.row.productionBatches[0].hcpCurrentFlowStartedAt : params.row.hcpCurrentFlowStartedAt)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Prazo
            </Typography>
            <Typography variant="body2" fontWeight={600}>
              {formatHCPDate(params.row.productionBatches?.[0] ? params.row.productionBatches[0].hcpCurrentFlowDeadline : params.row.hcpCurrentFlowDeadline)}
            </Typography>
          </Box>
        ),
      },
      {
        field: 'productionDueDate',
        headerName: 'Prazo',
        headerAlign: 'left',
        flex:1,
        renderCell: (params: any) => <Box sx={{ height: '100%', display: 'flex', alignItems: 'center'}}><Tag title={params?.row?.productionDueDate} boxStyle={{ backgroundColor: normalizeTimeStatusColors(params.row.dueDateStatus)?.bgColor }} titleStyle={{ color: normalizeTimeStatusColors(params.row.dueDateStatus)?.textColor, fontSize: '17px', fontWeight: '400' }} /></Box>
      },
   
      
      {
        field: 'actions',
        headerName: 'Etapa',
        headerAlign: 'center',
        width: 250,
        renderCell: (params: any) => <ButtonStepMenu readonly type='stage' options={[]} initialLabel={labelsOrderProductionStage[params.row.status as ServiceOrderStageEnum]} onChange={() => {}} serviceOrderProductId={params.row.id} initialValue={params.row.status as ServiceOrderStageEnum} />
      }
    ];
  
    return createColumns;
  }, []);


  useEffect(() => {
    if(params.orderId){
      handleShowOrder(params.orderId)
    }
  }, [])


  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  function a11yProps(index: number) {
    return {
      id: `simple-tab-${index}`,
      'aria-controls': `simple-tabpanel-${index}`,
    };
  }

  return (
    <Can permissions={['menu:financial:manage', 'menu:orders:manage']}>  
     <BaseScreen withSideBar={false}>
       <ConfirmDialog title='Pedido SEM vendedor' onCancel={() => setShowModalOrderWithoutSeller(false)} description='Você tem certeza?' open={showModalOrderWithoutSeller} onConfirm={handleUpdateOrderWithoutSeller}  /> 
      
      {showCustomersModal && <SelectSellerVariant 
        data={[]}
        onClose={() => setShowCustomersModal(false)}
        onSearch={() => {}}
        onSelect={handleSelectSeller}
        listSellersUseCase={listSellersUseCase}
      />}
      <Paper >
       
          <BaseContentPage 
              title={orderDetailsTitle}
              backButton
              backUrl={backUrl}
              breadcrumb={detailsBreadcrumb}
              sx={{ paddingBottom: 0, backgroundColor: theme.palette.primary.main}}
              minHeight='70px'
              titleBoxStyle={{ width: 'auto'}}
              Action={() =>  <Tabs value={value} onChange={handleChange} aria-label="Detalhes do pedido">
              <Tab label="Pedido" {...a11yProps(0)} />
              <Tab label="Produção" {...a11yProps(1)} />
              <Tab label="Financeiro" {...a11yProps(2)} />
        </Tabs>}
          >
            <Box />
             {/* Conteúdo das Tabs */}
            
        
        
        </BaseContentPage>
        </Paper>

        <Paper sx={{ bgcolor: 'background.paper', borderRadius: 1, px: 2, pb: 2 }}>
        {loading ? (
          <Box sx={{ py: 2 }}>
            <Grid2 container spacing={2}>
              <Grid2 size={{ xs: 12, lg: 8 }}>
                <Skeleton variant="rounded" height={280} sx={{ mb: 2, borderRadius: 1 }} />
                <Skeleton variant="rounded" height={140} sx={{ borderRadius: 1 }} />
              </Grid2>
              <Grid2 size={{ xs: 12, lg: 4 }}>
                <Skeleton variant="rounded" height={180} sx={{ mb: 2, borderRadius: 1 }} />
                <Skeleton variant="rounded" height={160} sx={{ mb: 2, borderRadius: 1 }} />
                <Skeleton variant="rounded" height={120} sx={{ borderRadius: 1 }} />
              </Grid2>
            </Grid2>
          </Box>
        ) : (
          <>
        <CustomTabPanel value={value} index={0} >
                <Grid2 container spacing={{ xs: 2, lg: 4 }} sx={{ alignItems: 'flex-start' }}>
                    <Grid2 size={{ xs: 12, lg: 8 }}>
                      <TabScrollArea>
                      {sale?.id ?  <SaleProductTable sale={sale as ISaleEntity}/> :  <Placeholder description="Erro ao carregar pedido." />}

                      <Paper sx={{ padding: '20px', marginTop: '30px'}} component="form" onSubmit={handleSubmit(handleUpdateObservations)}>
                        <Typography sx={{  fontSize: '1.5rem', fontWeight: 700, marginBottom: '15px' }}>Observações</Typography>
                        <Input
                            label="Observações"
                            placeholder="Preencha com as observações"
                            maxLength={500}
                            {...register("observations")}
                        />

                        <Button type="submit" sx={{ marginTop: 2, width: '100%', maxWidth: 350 }}>Atualizar Observações</Button>
                      </Paper>
                      </TabScrollArea>
                    </Grid2>

                    <Grid2 size={{ xs: 12, lg: 4 }}>
                      <TabScrollArea>
                      <Paper sx={{ padding: '20px', marginBottom: '20px'}}>
                        <Typography sx={{  fontSize: '1.5rem', fontWeight: 700, marginBottom: '15px' }}>Dados do cliente</Typography>

                          <Typography sx={{  fontSize: '1rem', fontWeight: 500, marginBottom: '5px' }}><b>Nome: </b>{sale?.customer?.name || 'Não informado'}</Typography>

                          <Typography sx={{  fontSize: '1rem', fontWeight: 500, marginBottom: '5px' }}><b>Documento: </b>{sale?.customer?.document || 'Não informado'}</Typography>

                          <Typography sx={{  fontSize: '1rem', fontWeight: 500, marginBottom: '5px' }}><b>Tipo: </b>{sale?.customer?.personType || 'Não informado'}</Typography>

                          <Typography sx={{  fontSize: '1rem', fontWeight: 500, marginBottom: '5px' }}><b>E-mail: </b>{sale?.customer?.email || 'Não informado'}</Typography>

                          <Typography sx={{  fontSize: '1rem', fontWeight: 500, marginBottom: '5px' }}><b>Fone: </b>{sale?.customer?.phone || 'Não informado'}</Typography>

                          {sale?.customer?.personType === "PJ" && <Typography sx={{  fontSize: '1rem', fontWeight: 500, marginBottom: '5px' }}><b>Inscrição Estadual: </b>{sale?.customer?.stateRegistration || 'Não informado'}</Typography>
                          }

                          <Button sx={{ marginTop: '20px'}} onClick={() => navigate(`/customers-form/${orderData?.customer?.id}`)}>Editar dados</Button>
                        </Paper>

                      <Paper sx={{ padding: '20px', marginBottom: '30px'}}>
                        <Typography sx={{  fontSize: '1.5rem', fontWeight: 700, marginBottom: '15px' }}>Fluxo de faturamento</Typography>

                        <FormControl fullWidth>
                          <InputLabel id="billing-flow-label">Fluxo</InputLabel>
                          <Select
                            labelId="billing-flow-label"
                            label="Fluxo"
                            value={currentBillingFlow}
                            disabled={isBillingFlowLocked}
                            onChange={(event) => handleUpdateBillingFlow(event.target.value as TBillingFlow)}
                          >
                            {billingFlowOptions.map(option => (
                              <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>

                        <Grid2 container spacing={2} sx={{ marginTop: '18px' }}>
                          <Grid2 size={{ xs: 12, md: 6 }}>
                            <FormControl fullWidth>
                              <InputLabel id="omie-billing-company-label">Empresa de Faturamento</InputLabel>
                              <Select
                                labelId="omie-billing-company-label"
                                label="Empresa de Faturamento"
                                value={selectedBillingCompanyId}
                                disabled={isOmieCompaniesLocked}
                                onChange={(event) => setSelectedBillingCompanyId(event.target.value as string)}
                              >
                                {billingCompanyOptions.map(option => (
                                  <MenuItem key={option.value} value={option.value ?? ''}>{option.label}</MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </Grid2>

                          <Grid2 size={{ xs: 12, md: 6 }}>
                            <FormControl fullWidth>
                              <InputLabel id="omie-receiving-company-label">Empresa de Recebimento</InputLabel>
                              <Select
                                labelId="omie-receiving-company-label"
                                label="Empresa de Recebimento"
                                value={selectedReceivingCompanyId ?? ''}
                                disabled={isOmieCompaniesLocked}
                                onChange={(event) => setSelectedReceivingCompanyId(event.target.value ? String(event.target.value) : null)}
                              >
                                {receivingCompanyOptions.map(option => (
                                  <MenuItem key={option.value ?? 'default'} value={option.value ?? ''}>{option.label}</MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </Grid2>

                          <Grid2 size={{ xs: 12 }}>
                            <Button
                              sx={{ width: { xs: '100%', md: 220 } }}
                              disabled={isOmieCompaniesLocked || loading}
                              isLoading={loading}
                              onClick={handleUpdateOrderOmieCompanies}
                            >
                              Salvar empresas
                            </Button>
                          </Grid2>
                        </Grid2>

                        <Box sx={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '14px' }}>
                          {orderData?.status && orderStatusColors && (
                            <Tag
                              title={normalizeOrderStatusTitle(orderData.status)}
                              boxStyle={{ backgroundColor: orderStatusColors.bgColor, borderRadius: '6px', height: '28px', padding: '0 12px' }}
                              titleStyle={{ color: orderStatusColors.textColor, fontSize: '12px', fontWeight: 700 }}
                            />
                          )}
                          <Tag
                            title={billingFlowLabels[currentBillingFlow]}
                            boxStyle={{ backgroundColor: billingFlowColor.backgroundColor, borderRadius: '6px', height: '28px', padding: '0 12px' }}
                            titleStyle={{ color: billingFlowColor.textColor, fontSize: '12px', fontWeight: 700 }}
                          />
                        </Box>

                        {couponCode && <Typography sx={{  fontSize: '0.9rem', fontWeight: 500, marginTop: '10px' }}><b>Cupom: </b>{couponCode}</Typography>}
                        {isBillingFlowLocked && <Typography sx={{  fontSize: '0.85rem', fontWeight: 500, marginTop: '10px', color: theme.palette.warning.main }}>O fluxo não pode ser alterado pois este pedido já está em processo de faturamento.</Typography>}
                        {isOmieCompaniesLocked && <Typography sx={{  fontSize: '0.85rem', fontWeight: 500, marginTop: '10px', color: theme.palette.warning.main }}>Não é permitido alterar as empresas Omie após iniciar o faturamento.</Typography>}
                        {billingWarningDescription && <Typography sx={{  fontSize: '0.85rem', fontWeight: 600, marginTop: '10px', color: theme.palette.warning.main }}>{billingWarningDescription}</Typography>}
                        {showBillingFlowSuggestion && flowSuggestion && (
                          <Notice
                            status="warning"
                            title="Sugestão do Hub"
                            description={`Identificamos o cupom ${couponCode}. Este pedido pode ser um ${billingFlowLabels[flowSuggestion]}.`}
                            buttonTitle={`Alterar para ${billingFlowLabels[flowSuggestion]}`}
                            action={() => handleUpdateBillingFlow(flowSuggestion)}
                            sx={{ marginTop: '14px' }}
                          />
                        )}
                      </Paper>

                      <Paper sx={{ padding: '20px', marginBottom: '20px'}}>
                        <SectionTitle>Resumo</SectionTitle>
                        <InfoLine label="Pedido NS" value={nsOrderNumber ? `#${nsOrderNumber}` : orderData?.nsOrderId ? `#${orderData.nsOrderId}` : undefined} />
                        <InfoLine label="Loja" value={orderData?.store} />
                        <InfoLine label="Criado em" value={formatOrderDate(orderData?.createdAt)} />
                        <InfoLine label="Produtos" value={orderData?.products?.length || 0} />
                        <Divider sx={{ margin: '12px 0' }} />
                        <InfoLine label="Original" value={formatOrderMoney(orderData?.originalValue)} />
                        <InfoLine label="Desconto" value={formatOrderMoney(orderData?.discountValue)} />
                        <InfoLine label="Frete" value={formatOrderMoney((orderData as any)?.nsShippingCost)} />
                        <InfoLine label="Total" value={formatOrderMoney(orderData?.totalValue)} />
                      </Paper>

                      <Paper sx={{ padding: '20px', marginBottom: '20px'}}>
                        <SectionTitle>Pagamento</SectionTitle>
                        <InfoLine label="Gateway" value={paymentDetails.gateway} />
                        <InfoLine label="Status" value={paymentDetails.status} />
                        <InfoLine label="Modalidade" value={paymentDetails.modality} />
                        <InfoLine label="Conta Omie" value={paymentDetails.omieBankAccountName} />
                        <InfoLine label="Valor final" value={paymentDetails.finalValue ? formatOrderMoney(paymentDetails.finalValue) : formatOrderMoney(orderData?.totalValue)} />
                        <InfoLine label="Data pagamento" value={paymentDetails.paymentDate ? formatOrderDate(paymentDetails.paymentDate) : undefined} />
                      </Paper>

                        <Paper sx={{ padding: '20px', marginTop: '30px'}}>
                          <Typography sx={{  fontSize: '1.5rem', fontWeight: 700, marginBottom: '15px' }}>Endereço de entrega</Typography>
                      
                          <Typography sx={{  fontSize: '1rem', fontWeight: 500, marginBottom: '5px' }}>{formatSaleAddress(sale?.shippingAddress)}</Typography>
                        </Paper>

                        <Paper sx={{ padding: '20px', marginTop: '30px'}}>
                          <Typography sx={{  fontSize: '1.5rem', fontWeight: 700, marginBottom: '15px' }}>Endereço de cobrança</Typography>
                        
                          <Typography sx={{  fontSize: '1rem', fontWeight: 500, marginBottom: '5px' }}>{formatSaleAddress(sale?.billingAddress)}</Typography>
                        </Paper>

                        <Paper sx={{ padding: '20px', marginTop: '30px'}}>
                        <Typography sx={{  fontSize: '1.5rem', fontWeight: 700, marginBottom: '15px' }}>Dados do Vendedor</Typography>

                        {orderData?.sellerAlreadyAdded ? <Typography sx={{  fontSize: '1rem', fontWeight: 500, marginBottom: '5px' }}>{orderData?.seller?.name || 'Pedido Sem Vendedor'}</Typography> : <Typography sx={{  fontSize: '1rem', fontWeight: 500, marginBottom: '5px' }}>Nenhum vendedor informado</Typography>}

                          <ButtonActionMenu customMenu={<Button sx={{ width: '100%', maxWidth: 250, marginTop: 2 }}>Atribuir Vendedor</Button>} options={[
                            {
                              title: 'Atribuir Vendedor',
                              onClick: () => setShowCustomersModal(true),
                              icon: <FaUser />
                            },
                            {
                              title: 'Pedido SEM Vendedor',
                              onClick: () => setShowModalOrderWithoutSeller(true),
                              icon: <FaUserAltSlash />
                            }
                          ]} />
                      
                        </Paper>

                        <Paper sx={{ p: 2.5, mt: 3, borderRadius: 2 }}>
                          <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
                            Histórico
                          </Typography>
                          <OrderHistoryTimeline
                            orderId={orderData?.id}
                            nsOrderId={orderData?.nsOrderId}
                            createdAt={orderData?.createdAt}
                            getOrderAuditTimelineUseCase={getOrderAuditTimelineUseCase}
                          />
                        </Paper>
                      </TabScrollArea>
                    </Grid2>
                </Grid2>
              
        </CustomTabPanel>
        
        <CustomTabPanel value={value} index={1} scrollable>
              {!!orderData?.products?.length ? (
                <Table 
                disableRowSelectionOnClick={false} 
                data={orderData?.products} 
                totalCount={orderData?.products?.length ?? 0} 
                columns={columnsProduction} 
                maxHeight="calc(100vh - 280px)"
                pagination={false}
                sortingMode="client"
                rowHeight={140}
                sx={{ width: '100%' }} 
                isRowSelectable={() => false}
              />
              ) : (
                <Placeholder description='Nenhum dado encontrado.' />
              )}
              {(orderData?.products ?? []).filter((product) => product.productionBatches?.length || product.productionSummary).map((product) => (
                <Paper key={product.id} variant="outlined" sx={{ mt: 2.5, p: { xs: 1.5, md: 2.5 }, borderRadius: 2 }}>
                  <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1.5 }}>
                    Lotes de produção · {product.productOrderCode ? `${product.productOrderCode} — ` : ''}{product.product?.name ?? 'Produto'}
                  </Typography>
                  <OrderProductionBatches batches={product.productionBatches} summary={product.productionSummary} commercialQuantity={product.totalQuantity ?? product.quantity} />
                </Paper>
              ))}
        </CustomTabPanel>

        <CustomTabPanel value={value} index={2} scrollable>
              {!!kanbanFinancialSchema?.columnOrder?.length ? <KanbanBoardFinancial setOmieOrderData={setOmieOrderData} omieDetailsOrder={omieOrderData} handleDetailsOmieOrder={handleDetailsOmieOrder} handleSelectProductToBill={handleSelectProductToBill} billProductsOrder={billProductsOrder} initialData={kanbanFinancialSchema}
              canClick={true}
              canDrag={false}
              productsToBill={productsToBill}
              handleSelectAllProductsToBill={handleSelectAllProductsToBill}
              billingWarningDescription={billingWarningDescription}
              /> : <Placeholder description='Nenhum dado encontrado.' /> }
          
        </CustomTabPanel>
          </>
        )}


        </Paper>
         
      </BaseScreen>
      </Can>
         
       

  );
};

export default FinancialDetails;
